import React, { useRef, useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
  LineChart as MuiLineChart,
  areaElementClasses,
  lineElementClasses,
} from "@mui/x-charts/LineChart";
import { ChartsReferenceLine } from "@mui/x-charts/ChartsReferenceLine";
import { formatChartTitle } from "../../utils";

interface LineChartData {
  [key: string]: string | number | null | undefined;
}

interface LineSeries {
  dataKey: string;
  label: string;
  color?: string;
  area?: boolean;
  stack?: string;
  dashed?: boolean;
}

interface LineChartProps {
  data: LineChartData[];
  xAxisKey?: string;
  series: LineSeries[];
  title?: string;
  height?: number;
  yAxisMin?: number;
  yAxisMax?: number;
  showMinMaxLines?: boolean;
  /**
   * When provided, this value is used for the min reference line instead of
   * computing min from the displayed (averaged) series data.  Use this to pass
   * the true minimum of the underlying raw data points returned by the API.
   */
  referenceMin?: number;
  /**
   * When provided, this value is used for the max reference line instead of
   * computing max from the displayed (averaged) series data.  Use this to pass
   * the true maximum of the underlying raw data points returned by the API.
   */
  referenceMax?: number;
}

/**
 * Calculates a sensible tick step for a Y-axis given the data range.
 * Aims for approximately 5 ticks within the range.
 */
function calculateTickStep(min: number, max: number): number {
  const range = max - min;
  if (range <= 0) return 1;
  const rawStep = range / 5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  let niceStep: number;
  if (normalized <= 1) niceStep = 1;
  else if (normalized <= 2) niceStep = 2;
  else if (normalized <= 5) niceStep = 5;
  else niceStep = 10;
  return niceStep * magnitude;
}

const AreaGradient: React.FC<{ id: string; color: string }> = ({
  id,
  color,
}) => {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.3} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
};

let chartInstanceCounter = 0;

export const LineChart: React.FC<LineChartProps> = ({
  data,
  xAxisKey,
  series,
  title,
  height = 300,
  yAxisMin,
  yAxisMax,
  showMinMaxLines = false,
  referenceMin,
  referenceMax,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const formattedTitle = formatChartTitle(title);

  const detectedXAxisKey = useMemo(() => {
    if (xAxisKey) return xAxisKey;
    if (!data || data.length === 0) return "x";

    const firstItem = data[0];
    const seriesKeys = new Set(series.map((s) => s.dataKey));

    const possibleKeys = ["label", "period", "day", "date", "xAxis", "x"];
    for (const key of possibleKeys) {
      if (firstItem[key] !== undefined && !seriesKeys.has(key)) {
        return key;
      }
    }

    const allKeys = Object.keys(firstItem);
    const nonSeriesKey = allKeys.find((key) => !seriesKeys.has(key));
    return nonSeriesKey || "x";
  }, [data, series, xAxisKey]);

  const chartIdRef = useRef<string>(
    `line-chart-${Date.now()}-${++chartInstanceCounter}-${Math.random()
      .toString(36)
      .substring(2, 9)}`
  );

  const chartId = chartIdRef.current;

  // Compute actual data min/max across all series for dynamic scaling
  const { dataMin, dataMax } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const item of data) {
      for (const s of series) {
        const val = item[s.dataKey];
        if (typeof val === "number" && isFinite(val)) {
          if (val < min) min = val;
          if (val > max) max = val;
        }
      }
    }
    return {
      dataMin: min === Infinity ? 0 : min,
      dataMax: max === -Infinity ? 100 : max,
    };
  }, [data, series]);

  // Determine effective Y-axis bounds with padding.
  // When showMinMaxLines is active the axis must extend at least to the
  // reference line values plus a small buffer so the labels are never clipped.
  const effectiveYMin = useMemo(() => {
    const range = dataMax - dataMin;
    const padding = Math.max(range * 0.05, 1);
    let base: number;
    if (yAxisMin !== undefined) {
      base = yAxisMin;
    } else {
      base = Math.max(0, Math.floor(dataMin - padding));
    }
    if (showMinMaxLines && referenceMin !== undefined) {
      base = Math.min(base, Math.floor(referenceMin - padding));
    }
    return base;
  }, [yAxisMin, dataMin, dataMax, showMinMaxLines, referenceMin]);

  const effectiveYMax = useMemo(() => {
    const range = dataMax - dataMin;
    const padding = Math.max(range * 0.05, 1);
    let base: number;
    if (yAxisMax !== undefined) {
      base = yAxisMax;
    } else {
      base = Math.ceil(dataMax + padding);
    }
    if (showMinMaxLines) {
      const refMaxValue = referenceMax !== undefined ? referenceMax : dataMax;
      base = Math.max(base, Math.ceil(refMaxValue + padding));
    }
    return base;
  }, [yAxisMax, dataMin, dataMax, showMinMaxLines, referenceMax]);

  const tickMinStep = useMemo(() => {
    return calculateTickStep(effectiveYMin, effectiveYMax);
  }, [effectiveYMin, effectiveYMax]);

  const areaSeriesIndices = useMemo(
    () =>
      series
        .map((s, index) => (s.area ? index : null))
        .filter((index): index is number => index !== null),
    [series]
  );

  const muiSeries = useMemo(
    () =>
      series.map((s, index) => ({
        id: `series-${chartId}-${index}`,
        dataKey: s.dataKey,
        label: s.label,
        color: s.color || theme.palette.primary.main,
        area: s.area || false,
        stack: s.stack,
        connectNulls: true,
      })),
    [series, theme.palette.primary.main, chartId]
  );

  const dashedSeriesIds = useMemo(
    () =>
      series
        .map((s, index) => (s.dashed ? `series-${chartId}-${index}` : null))
        .filter((id): id is string => id !== null),
    [series, chartId]
  );

  const areaSeriesIds = useMemo(
    () =>
      series
        .map((s, index) => (s.area ? `series-${chartId}-${index}` : null))
        .filter((id): id is string => id !== null),
    [series, chartId]
  );

  const chartSx = useMemo(() => {
    const sx: Record<string, unknown> = {};

    if (dashedSeriesIds.length > 0) {
      dashedSeriesIds.forEach((seriesId) => {
        sx[`& .${lineElementClasses.root}[data-series-id="${seriesId}"]`] = {
          strokeDasharray: "5 5",
        };
        sx[`& .${lineElementClasses.root}[data-series="${seriesId}"]`] = {
          strokeDasharray: "5 5",
        };
      });
    }

    if (areaSeriesIds.length > 0) {
      areaSeriesIds.forEach((seriesId) => {
        const seriesIndex = series.findIndex(
          (_, idx) => `series-${chartId}-${idx}` === seriesId
        );
        const seriesConfig = series[seriesIndex];
        const color = seriesConfig.color || theme.palette.primary.main;
        const gradientId = `area-gradient-${chartId}-${seriesIndex}`;

        sx[`& .${areaElementClasses.root}[data-series-id="${seriesId}"]`] = {
          fill: `url(#${gradientId})`,
        };
        sx[`& .${areaElementClasses.root}[data-series="${seriesId}"]`] = {
          fill: `url(#${gradientId})`,
        };
      });
    }

    return sx;
  }, [
    dashedSeriesIds,
    areaSeriesIds,
    series,
    chartId,
    theme.palette.primary.main,
  ]);

  const getGradientIdForSeries = (seriesIndex: number) =>
    `area-gradient-${chartId}-${seriesIndex}`;

  // Use externally-provided reference values (raw data min/max from API) when available,
  // otherwise fall back to computing from the displayed averaged series data.
  const refMin = referenceMin !== undefined ? referenceMin : dataMin;
  const refMax = referenceMax !== undefined ? referenceMax : dataMax;

  const minLabel = `Min: ${Math.round(refMin * 10) / 10}`;
  const maxLabel = `Max: ${Math.round(refMax * 10) / 10}`;

  return (
    <Box ref={containerRef} sx={{ width: "100%" }}>
      {formattedTitle && (
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {formattedTitle}
        </Typography>
      )}
      <Box ref={chartRef}>
        <MuiLineChart
          dataset={data}
          height={height}
          xAxis={[
            {
              scaleType: "point",
              dataKey: detectedXAxisKey,
              disableTicks: true,
            },
          ]}
          yAxis={[
            {
              min: effectiveYMin,
              max: effectiveYMax,
              disableTicks: true,
              tickMinStep: tickMinStep,
            },
          ]}
          series={muiSeries}
          margin={{ left: 0, right: 0, top: 20, bottom: 20 }}
          grid={{ vertical: true, horizontal: true }}
          sx={chartSx}
        >
          {areaSeriesIndices.map((seriesIndex) => {
            const seriesConfig = series[seriesIndex];
            const color = seriesConfig.color || theme.palette.primary.main;
            return (
              <AreaGradient
                key={`gradient-${seriesIndex}`}
                id={getGradientIdForSeries(seriesIndex)}
                color={color}
              />
            );
          })}
          {showMinMaxLines && data.length > 0 && refMin !== refMax && (
            <>
              <ChartsReferenceLine
                y={refMax}
                label={maxLabel}
                labelAlign="end"
                lineStyle={{
                  stroke: theme.palette.text.secondary,
                  strokeDasharray: "6 3",
                  strokeWidth: 1.5,
                  opacity: 0.7,
                }}
                labelStyle={{
                  fontSize: 11,
                  fill: theme.palette.text.secondary,
                }}
              />
              <ChartsReferenceLine
                y={refMin}
                label={minLabel}
                labelAlign="end"
                lineStyle={{
                  stroke: theme.palette.text.secondary,
                  strokeDasharray: "6 3",
                  strokeWidth: 1.5,
                  opacity: 0.7,
                }}
                labelStyle={{
                  fontSize: 11,
                  fill: theme.palette.text.secondary,
                }}
              />
            </>
          )}
        </MuiLineChart>
      </Box>
    </Box>
  );
};
